import React from 'react';
import { Popover, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import NumberField from 'components/library/NumberField';

/**
 * Common Popup for selecting batch quantities for a given item.
 * Supports highlighting expired and soon-to-expire batches.
 */
const BatchSelectionPopup = ({
  open,
  anchorEl,
  onClose,
  item,
  stockBatches,
  selectedBatches,
  originalBatchQtys = {},
  onChange,
  showExpired = true,
}) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');

  // Filter batches by item ID and optionally filter out expired ones
  const filteredBatches = (stockBatches || []).filter((b) => {
    const isSameItem = String(b.item) === String(item?.id);
    if (!isSameItem) return false;
    
    if (!showExpired) {
      // Use direct Date comparison and treat 'Z' from backend as local time for accurate comparison
      const expiryDate = b.expiry_date ? new Date(b.expiry_date.replace('Z', '')) : null;
      const isExpired = expiryDate && expiryDate <= new Date();
      if (isExpired) return false;
    }
    return true;
  });

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <Box p={2} style={{ minWidth: '320px' }}>
        <Typography variant="h6" gutterBottom style={{ fontWeight: 600, fontSize: '1rem' }}>
          {tOperations('Select Batches')}
        </Typography>
        {filteredBatches.map((batch) => {
          // Precise date/time comparison treating timestamps as local
          const expiryDate = batch.expiry_date ? new Date(batch.expiry_date.replace('Z', '')) : null;
          const now = new Date();
          const diff = expiryDate ? expiryDate - now : null;

          const isExpired = diff !== null && diff <= 0;
          const alertDurationMs = (Number(item?.expiry_alert_duration_in_hours) || 0) * 60 * 60 * 1000;
          const isWarning = !isExpired && diff !== null && diff > 0 && diff <= alertDurationMs;

          // Distinct colors: Light Red for Expired, Light Orange for Warning
          const backgroundColor = isExpired ? '#ffebee' : isWarning ? '#fff3e0' : 'transparent';
          const borderColor = isExpired ? '#ffcdd2' : isWarning ? '#ffe0b2' : 'none';

          return (
            <Box
              key={batch.id}
              mb={1}
              p={1}
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
              style={{
                backgroundColor,
                color: isExpired ? '#b71c1c' : 'inherit', // Darker red text for expired
                borderRadius: '6px',
                border: borderColor !== 'none' ? `1px solid ${borderColor}` : 'none',
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  style={{ fontWeight: 600, color: 'inherit' }}
                >
                  {batch.batch_name || batch.batch_no} {isExpired && `(${tCommon('Expired')})`}
                </Typography>
                <Typography variant="caption" style={{ color: isExpired ? '#333' : 'rgba(0, 0, 0, 0.6)' }}>
                  {tCommon('Stock')}: {((batch.stock || 0) + (originalBatchQtys[String(batch.id)] || 0)).toFixed(3)} |{' '}
                  {tCommon('Expiry')}: {batch.expiry_date ? DateTimeHelpers.formatDateTime(batch.expiry_date) : 'N/A'}
                </Typography>
              </Box>
              <NumberField
                label={tCommon('Qty')}
                value={(selectedBatches || []).find((sb) => String(sb.batch_id) === String(batch.id))?.quantity || ''}
                onChange={(e) => onChange(batch.id, e.target.value)}
                style={{ width: '80px' }}
                size="small"
              />
            </Box>
          );
        })}
        {filteredBatches.length === 0 && (
          <Typography variant="body2" color="error" style={{ textAlign: 'center', py: 2 }}>
            {tOperations('No batches available for this item')}
          </Typography>
        )}
      </Box>
    </Popover>
  );
};

export default BatchSelectionPopup;
